import { type ReactNode, createContext, useContext } from "react"

/**
 * OKS-1038 — where a brand collab transition should land after success.
 *
 * The brand collab dialogs (`MarkCompleteConfirmDialog`, `FlagIssueDialog`, …)
 * post to `MissionCollabsController#transitions`, which by default redirects to
 * the mission page. The crew member page mounts the same dialogs and wants the
 * member page back, with the rows it had loaded.
 *
 * A context rather than a prop threaded through nine dialogs: the mission panel
 * mounts no provider, so `<ReturnToFields>` renders nothing there and the
 * panel's forms are byte-for-byte what they were.
 *
 * ⚠️ The server treats `return_to` as an allow-listed VALUE and derives the
 * destination from the collab itself (`RETURN_TO_DESTINATIONS`). Nothing here
 * is a URL, and `collabs_shown` is clamped server-side.
 */
export interface CollabTransitionReturn {
  returnTo: "crew_member"
  /** How many member-page rows are loaded, so the redirect keeps them. */
  collabsShown: number
}

const CollabTransitionReturnContext =
  createContext<CollabTransitionReturn | null>(null)

export function CollabTransitionReturnProvider({
  value,
  children,
}: {
  value: CollabTransitionReturn
  children: ReactNode
}) {
  return (
    <CollabTransitionReturnContext.Provider value={value}>
      {children}
    </CollabTransitionReturnContext.Provider>
  )
}

/** Hidden inputs for a transition `<Form>`; nothing without a provider. */
export function ReturnToFields() {
  const value = useContext(CollabTransitionReturnContext)
  if (!value) return null

  return (
    <>
      <input type="hidden" name="return_to" value={value.returnTo} />
      <input
        type="hidden"
        name="collabs_shown"
        value={String(value.collabsShown)}
      />
    </>
  )
}
